<template>
  <AiEducationLayout>
    <div class="ai-edu-admin-page">
      <ContentWrap title="考勤中心">
        <el-form :inline="true" :model="query" class="-mb-15px">
          <el-form-item label="学校">
            <el-select
              v-model="query.schoolId"
              class="!w-200px"
              clearable
              filterable
              @change="onSchoolChange"
            >
              <el-option v-for="s in schools" :key="s.id" :label="s.name" :value="s.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="教学班">
            <el-select
              v-model="query.teachingClassId"
              class="!w-220px"
              clearable
              filterable
              @change="handleQuery"
            >
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
            <el-button
              v-hasPermi="['ai-education:attendance:write']"
              :disabled="!query.teachingClassId"
              plain
              type="primary"
              @click="openSessionDialog()"
            >
              <Icon icon="ep:plus" />新建课次
            </el-button>
          </el-form-item>
        </el-form>
      </ContentWrap>
      <ContentWrap>
        <el-table v-loading="loading" :data="sessions" border>
          <el-table-column prop="title" label="课次标题" min-width="160" />
          <el-table-column prop="sessionNo" label="课次" width="80" />
          <el-table-column prop="startTime" label="开始" width="170" />
          <el-table-column prop="endTime" label="结束" width="170" />
          <el-table-column prop="checkinDeadline" label="签到截止" width="170" />
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_ATTENDANCE_SESSION_STATUS" :value="row.status" />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="260" fixed="right">
            <template #default="{ row }">
              <el-button
                v-hasPermi="['ai-education:attendance:write']"
                link
                type="success"
                @click="handleOpen(row.id)"
                >开启签到</el-button
              >
              <el-button link type="primary" @click="viewRecords(row)">明细</el-button>
              <el-button
                v-hasPermi="['ai-education:attendance:write']"
                link
                type="warning"
                @click="openMakeup(row)"
                >补登</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </ContentWrap>

      <Dialog v-model="sessionVisible" :title="sessionForm.id ? '编辑课次' : '新建课次'" width="520px">
        <el-form ref="sessionFormRef" :model="sessionForm" :rules="sessionRules" label-width="110px">
          <el-form-item label="教学班" prop="teachingClassId">
            <el-select v-model="sessionForm.teachingClassId" class="w-100%" filterable>
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="标题" prop="title"><el-input v-model="sessionForm.title" /></el-form-item>
          <el-form-item label="课次">
            <el-input-number v-model="sessionForm.sessionNo" :min="1" />
          </el-form-item>
          <el-form-item label="开始时间" prop="startTime">
            <el-date-picker
              v-model="sessionForm.startTime"
              class="!w-100%"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
            />
          </el-form-item>
          <el-form-item label="结束时间" prop="endTime">
            <el-date-picker
              v-model="sessionForm.endTime"
              class="!w-100%"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
            />
          </el-form-item>
          <el-form-item label="签到截止">
            <el-date-picker
              v-model="sessionForm.checkinDeadline"
              class="!w-100%"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="sessionVisible = false">取消</el-button>
          <el-button :loading="saving" type="primary" @click="submitSession">保存</el-button>
        </template>
      </Dialog>

      <Dialog v-model="recordVisible" title="考勤明细" width="720px">
        <el-table :data="records" border max-height="420">
          <el-table-column label="学生" min-width="120">
            <template #default="{ row }">{{ studentLabel(row) }}</template>
          </el-table-column>
          <el-table-column label="结果" width="100">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_ATTENDANCE_RESULT" :value="row.result" />
            </template>
          </el-table-column>
          <el-table-column prop="checkinTime" label="签到时间" width="170" />
          <el-table-column label="来源" width="110">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_ATTENDANCE_SOURCE" :value="row.source" />
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="120" />
        </el-table>
      </Dialog>

      <Dialog v-model="makeupVisible" title="补登" width="480px">
        <el-form ref="makeupFormRef" :model="makeupForm" :rules="makeupRules" label-width="100px">
          <el-form-item label="学生" prop="studentId">
            <el-select
              v-model="makeupForm.studentId"
              class="w-100%"
              filterable
              remote
              :remote-method="searchStudents"
              :loading="studentLoading"
            >
              <el-option
                v-for="s in students"
                :key="s.id"
                :label="s.nickname || s.mobile || `#${s.id}`"
                :value="s.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="结果" prop="result">
            <el-select v-model="makeupForm.result" class="w-100%">
              <el-option
                v-for="d in getIntDictOptions(DICT_TYPE.AI_EDU_ATTENDANCE_RESULT)"
                :key="d.value"
                :label="d.label"
                :value="d.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="备注"><el-input v-model="makeupForm.remark" /></el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="makeupVisible = false">取消</el-button>
          <el-button :loading="saving" type="primary" @click="submitMakeup">确定</el-button>
        </template>
      </Dialog>
    </div>
  </AiEducationLayout>
</template>

<script lang="ts" setup>
import * as AiEducationApi from '@/api/aiEducation'
import * as UniversityApi from '@/api/aiEducation/university'
import AiEducationLayout from '../../layout/AiEducationLayout.vue'
import request from '@/config/axios'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'AiEduAttendance' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const studentLoading = ref(false)
const schools = ref<AiEducationApi.SchoolVO[]>([])
const teachingClasses = ref<AiEducationApi.TeachingClassVO[]>([])
const sessions = ref<UniversityApi.AttendanceSessionVO[]>([])
const records = ref<UniversityApi.AttendanceRecordVO[]>([])
const students = ref<Array<{ id: number; nickname?: string; mobile?: string }>>([])
const query = reactive<{ schoolId?: number; teachingClassId?: number }>({})
const sessionVisible = ref(false)
const recordVisible = ref(false)
const makeupVisible = ref(false)
const sessionFormRef = ref<FormInstance>()
const makeupFormRef = ref<FormInstance>()
const sessionForm = reactive<Partial<UniversityApi.AttendanceSessionVO>>({})
const makeupForm = reactive<{ sessionId?: number; studentId?: number; result?: number; remark?: string }>(
  {}
)
const sessionRules: FormRules = {
  teachingClassId: [{ required: true, message: '请选择教学班', trigger: 'change' }],
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  startTime: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
  endTime: [{ required: true, message: '请选择结束时间', trigger: 'change' }]
}
const makeupRules: FormRules = {
  studentId: [{ required: true, message: '请选择学生', trigger: 'change' }],
  result: [{ required: true, message: '请选择结果', trigger: 'change' }]
}

const studentLabel = (row: UniversityApi.AttendanceRecordVO) => {
  if (row.studentName) return row.studentName
  const s = students.value.find((item) => item.id === row.studentId)
  return s?.nickname || s?.mobile || `学生 #${row.studentId}`
}

const onSchoolChange = async () => {
  query.teachingClassId = undefined
  teachingClasses.value = query.schoolId
    ? (await AiEducationApi.getTeachingClassList({ schoolId: query.schoolId })) || []
    : []
  sessions.value = []
}
const load = async () => {
  if (!query.teachingClassId) {
    sessions.value = []
    return
  }
  loading.value = true
  try {
    sessions.value = (await UniversityApi.getAttendanceSessionList(query.teachingClassId)) || []
  } finally {
    loading.value = false
  }
}
const handleQuery = () => load()
const openSessionDialog = (row?: UniversityApi.AttendanceSessionVO) => {
  Object.assign(sessionForm, {
    id: row?.id,
    teachingClassId: row?.teachingClassId ?? query.teachingClassId,
    title: row?.title || '',
    sessionNo: row?.sessionNo,
    startTime: row?.startTime,
    endTime: row?.endTime,
    checkinDeadline: row?.checkinDeadline
  })
  sessionVisible.value = true
}
const submitSession = async () => {
  await sessionFormRef.value?.validate()
  saving.value = true
  try {
    await UniversityApi.saveAttendanceSession(sessionForm)
    message.success('保存成功')
    sessionVisible.value = false
    await load()
  } finally {
    saving.value = false
  }
}
const handleOpen = async (sessionId?: number) => {
  if (!sessionId) return
  await UniversityApi.openAttendanceCheckin(sessionId)
  message.success('已开启签到')
  await load()
}
const viewRecords = async (row: UniversityApi.AttendanceSessionVO) => {
  if (!row.id) return
  records.value = (await UniversityApi.getAttendanceRecords(row.id)) || []
  await searchStudents()
  recordVisible.value = true
}
const searchStudents = async (keyword = '') => {
  studentLoading.value = true
  try {
    const result: any = await request.get({
      url: '/member/user/page',
      params: { pageNo: 1, pageSize: 30, nickname: keyword || undefined }
    })
    students.value = result?.list || result || []
  } finally {
    studentLoading.value = false
  }
}
const openMakeup = async (row: UniversityApi.AttendanceSessionVO) => {
  makeupForm.sessionId = row.id
  makeupForm.studentId = undefined
  makeupForm.result = 10
  makeupForm.remark = ''
  await searchStudents()
  makeupVisible.value = true
}
const submitMakeup = async () => {
  await makeupFormRef.value?.validate()
  saving.value = true
  try {
    await UniversityApi.makeupAttendance({
      sessionId: makeupForm.sessionId!,
      studentId: makeupForm.studentId!,
      result: makeupForm.result!,
      remark: makeupForm.remark
    })
    message.success('补登成功')
    makeupVisible.value = false
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  schools.value = (await AiEducationApi.getSchoolList()) || []
})
</script>
